/**
 * DQ Workbench - Notes tab.
 *
 * A notes-activity list: open exception rows that currently carry a note
 * thread, newest activity first. Reuses the cross-FP
 * ``/admin/exceptions/activity`` endpoint with ``tab='active'`` (the
 * backend's "note threads on open rows" pillar) - no dedicated notes
 * endpoint. Each row opens the shared ``ExceptionDrawer`` with the thread
 * expanded so a reviewer can read, reply, edit, or act without leaving the
 * admin panel.
 *
 * Scope note: ``tab='active'`` is open-rows-with-notes; notes on already
 * resolved/ignored rows are out of scope here (would need a ``has_notes``
 * filter on the route).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'

import { useDataExceptionsCtx } from '../DataExceptionsContext'
import {
  exceptionActivityApi,
  type ExceptionActivityListResponse,
} from '../../../../services/exceptionActivityApi'
import { useDataFetching } from '../../../../hooks/useDataFetching'
import DataFetchWrapper from '../../../../components/inspector/shared/DataFetchWrapper'
import { cardChrome, SectionLabel } from '../components/chrome'
import ActivityFeed from '../components/ActivityFeed'
import AdminExceptionDrawerHost, {
  useAdminExceptionDrawer,
} from '../components/AdminExceptionDrawerHost'

const PAGE_SIZE = 25

const Notes: React.FC = () => {
  const { market, refreshKey, refresh } = useDataExceptionsCtx()
  const drawer = useAdminExceptionDrawer()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  // Debounce the search box so we issue one request after typing settles,
  // not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => clearTimeout(t)
  }, [searchInput])

  // Any filter change resets to page 1.
  useEffect(() => {
    setPage(1)
  }, [market, search])

  const { data, loading, error } = useDataFetching<ExceptionActivityListResponse>({
    fetchFn: useCallback(
      () =>
        exceptionActivityApi.getList({
          tab: 'active',
          market: market || undefined,
          search: search || undefined,
          sort_by: 'last_activity_at',
          sort_dir: 'desc',
          page,
          page_size: PAGE_SIZE,
        }),
      [market, search, page],
    ),
    dependencies: [market, search, page, refreshKey],
  })

  const items = data?.items ?? []
  const totalPages = data?.total_pages ?? 0
  const total = data?.total ?? 0

  const countLabel = useMemo(() => {
    if (loading || total === 0) return null
    return total === 1 ? '1 note thread' : `${total} note threads`
  }, [loading, total])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search franchise, reference, or reason"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {countLabel && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {countLabel}
          </Typography>
        )}
      </Box>

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
        Open exceptions with an active note thread, newest activity first. Click a row to read the
        thread and reply, edit, or act.
      </Typography>

      <SectionLabel>Note activity</SectionLabel>
      <Card sx={cardChrome}>
        <CardContent>
          <DataFetchWrapper loading={loading} error={error} data={items}>
            <ActivityFeed items={items} onSelect={drawer.open} />
          </DataFetchWrapper>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_e, value) => setPage(value)}
                size="small"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <AdminExceptionDrawerHost
        selected={drawer.selected}
        onClose={drawer.close}
        onAfterMutation={refresh}
      />
    </Box>
  )
}

export default Notes
